import { eventEmitter } from '../index';
import { WsFlightNotification } from 'types/flights';
import { tokenManager } from 'core/redux/api/authorization';
import { EventEmitterMessages } from 'constants/event-emitter';

type NotificationMessage = {
  '@type': string;
  batchSize: number;
  notification?: WsFlightNotification;
};

export class WsService {
  private ws: WebSocket | null = null;

  private batchSize: number | null = null;
  private receivedCount = 0;

  private reconnectTimer: NodeJS.Timeout | null = null;
  private manuallyClosed = false;

  private desiredSubscriptions = new Set<string>();

  private resetBatch() {
    this.batchSize = null;
    this.receivedCount = 0;
  }

  private setBatchSize(size: number) {
    this.batchSize = size;
    this.receivedCount = 0;
  }

  private acknowledgeBatch() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || this.batchSize === null) return;

    this.ws.send(JSON.stringify({ '@type': 'NotificationAcknowledge', quantity: this.batchSize }));

    this.resetBatch();
  }

  subscribeToFlight(uri: string) {
    this.desiredSubscriptions.add(uri);
    this.send({ '@type': 'Subscribe', subscription: { '@type': 'FlightLegSubscription', uri } });
  }

  unsubscribeToFlight(uri: string) {
    this.desiredSubscriptions.delete(uri);
    this.send({ '@type': 'Unsubscribe', subscription: { '@type': 'FlightLegSubscription', uri } });
  }

  closeConnection() {
    this.manuallyClosed = true;
    this.resetBatch();

    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);

    if (this.ws) {
      this.ws.close(1000, 'manual');
      this.ws = null;
    }
  }

  private scheduleReconnect() {
    if (this.manuallyClosed || this.reconnectTimer) return;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, 3000);
  }

  private send(payload: unknown) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(payload));
  }

  connect() {
    if (this.ws) return;

    this.manuallyClosed = false;
    const token = tokenManager.getAccessToken()?.jwt;

    if (!token) return;

    this.ws = new WebSocket(`${process.env.REACT_APP_WS_URL}/v3/datacaster`, [
      `ow-auth-token`,
      `ow-auth-token-${token}`,
    ]);

    this.ws.onopen = () => {
      this.resetBatch();

      this.desiredSubscriptions.forEach((uri) => {
        this.send({ '@type': 'Subscribe', subscription: { '@type': 'FlightLegSubscription', uri } });
      });
    };

    this.ws.onmessage = (event) => {
      let data: NotificationMessage;

      try {
        data = JSON.parse(event.data);
      } catch (err) {
        console.error('❌ WS parse error:', err);
        return;
      }

      if (this.batchSize === null) {
        this.setBatchSize(data.batchSize);
      }

      if (data.notification && 'flightLegInfo' in data.notification) {
        eventEmitter.emit(EventEmitterMessages.FlightUpdate, data.notification.flightLegInfo);
      }

      this.receivedCount++;

      if (this.batchSize && this.receivedCount >= this.batchSize) {
        this.acknowledgeBatch();
      }
    };

    this.ws.onerror = (err) => {
      console.log('❌ WebSocket error:', err);
    };

    this.ws.onclose = (event) => {
      this.ws = null;
      this.resetBatch();
      if (!this.manuallyClosed) this.scheduleReconnect();
    };
  }
}
